import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { cn } from '../utils/cn';

const priorities = [
    { value: 'low', label: 'Low', color: 'bg-blue-500' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-500' },
    { value: 'high', label: 'High', color: 'bg-orange-500' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-500' },
];

const categories = [
    { value: 'work', label: 'Work' },
    { value: 'personal', label: 'Personal' },
    { value: 'shopping', label: 'Shopping' },
    { value: 'health', label: 'Health' },
    { value: 'other', label: 'Other' },
];

export function TaskModal({ isOpen, onClose, onSubmit, task = null }) {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        priority: 'medium',
        category: 'personal',
        dueDate: '',
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (task) {
            setFormData({
                title: task.title || '',
                description: task.description || '',
                priority: task.priority || 'medium',
                category: task.category || 'personal',
                dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
            });
        } else {
            setFormData({
                title: '',
                description: '',
                priority: 'medium',
                category: 'personal',
                dueDate: '',
            });
        }
        setErrors({});
    }, [task, isOpen]);

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            onSubmit(formData);
            onClose();
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-slide-up">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900">
                        {task ? 'Edit Task' : 'Create New Task'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <Input
                        label="Title"
                        value={formData.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        error={errors.title}
                        placeholder="Enter task title"
                    />

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="Enter task description (optional)"
                            rows={3}
                            className={cn(
                                'w-full px-4 py-2 border border-gray-300 rounded-lg',
                                'focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                                'outline-none transition-all duration-200 resize-none'
                            )}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Priority
                            </label>
                            <div className="space-y-2">
                                {priorities.map((p) => (
                                    <label
                                        key={p.value}
                                        className={cn(
                                            'flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors',
                                            formData.priority === p.value
                                                ? 'bg-gray-100'
                                                : 'hover:bg-gray-50'
                                        )}
                                    >
                                        <input
                                            type="radio"
                                            name="priority"
                                            value={p.value}
                                            checked={formData.priority === p.value}
                                            onChange={(e) => handleChange('priority', e.target.value)}
                                            className="sr-only"
                                        />
                                        <span className={cn('w-3 h-3 rounded-full', p.color)} />
                                        <span className="text-sm font-medium text-gray-700">
                                            {p.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Category
                            </label>
                            <select
                                value={formData.category}
                                onChange={(e) => handleChange('category', e.target.value)}
                                className={cn(
                                    'w-full px-3 py-2 border border-gray-300 rounded-lg',
                                    'focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                                    'outline-none transition-all duration-200'
                                )}
                            >
                                {categories.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>

                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Due Date
                                </label>
                                <input
                                    type="date"
                                    value={formData.dueDate}
                                    onChange={(e) => handleChange('dueDate', e.target.value)}
                                    className={cn(
                                        'w-full px-3 py-2 border border-gray-300 rounded-lg',
                                        'focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                                        'outline-none transition-all duration-200'
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onClose}
                            className="flex-1"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="flex-1"
                        >
                            {task ? 'Update Task' : 'Create Task'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}